import { ChangeDetectionStrategy, Component, computed, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { v4 as uuid } from 'uuid';

import type { DataInputConfig } from '../../../widgets/model';
import {
  BIMODAL_DISTRIBUTIONS,
  DECIMAL_PLACES_OPTIONS,
  DEFAULT_GENERATOR_CONFIG,
  DISTRIBUTION_CARDS,
  MEAN_SD_DISTRIBUTIONS,
  SAMPLE_SIZE_PRESETS,
  type DistributionShape,
} from './descriptive-generator.model';
import { generateData } from './distributions.util';
import { type LibraryDataset, LIBRARY_DATASETS } from './library-datasets.model';

@Component({
  selector: 'dojo-descriptive-generator',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatExpansionModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
  ],
  templateUrl: './descriptive-generator.component.html',
  styleUrl: './descriptive-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DescriptiveGeneratorComponent {
  readonly generate = output<DataInputConfig>();
  readonly closePanel = output<void>();

  // Library
  readonly libraryExpanded = signal(true);
  readonly customExpanded = signal(false);
  readonly libraryDatasets = LIBRARY_DATASETS;

  // Distribution
  readonly distribution = signal<DistributionShape>(DEFAULT_GENERATOR_CONFIG.distribution);
  readonly distributionCards = DISTRIBUTION_CARDS;

  // Sample size
  readonly sampleSize = signal(DEFAULT_GENERATOR_CONFIG.sampleSize);
  readonly sampleSizePresets = SAMPLE_SIZE_PRESETS;

  // Parameters — min/max for range-based distributions, mean/sd for normal/bimodal
  readonly minValueCtrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.minValue, { nonNullable: true });
  readonly maxValueCtrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.maxValue, { nonNullable: true });
  readonly meanCtrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.mean, { nonNullable: true });
  readonly stdDevCtrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.stdDev, { nonNullable: true });
  readonly center1Ctrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.center1, { nonNullable: true });
  readonly center2Ctrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.center2, { nonNullable: true });
  readonly spreadCtrl = new FormControl<number>(DEFAULT_GENERATOR_CONFIG.spread, { nonNullable: true });

  readonly usesMeanSd = computed(() => MEAN_SD_DISTRIBUTIONS.has(this.distribution()));
  readonly isBimodal = computed(() => BIMODAL_DISTRIBUTIONS.has(this.distribution()));

  // Outliers
  readonly outliers = signal(DEFAULT_GENERATOR_CONFIG.outliers);

  // Decimal places
  readonly decimalPlaces = signal(DEFAULT_GENERATOR_CONFIG.decimalPlaces);
  readonly decimalPlacesOptions = DECIMAL_PLACES_OPTIONS;

  // Advanced
  readonly advancedOpen = signal(false);
  readonly seedCtrl = new FormControl<string>('', { nonNullable: true });
  readonly columnLabelCtrl = new FormControl<string>(DEFAULT_GENERATOR_CONFIG.columnLabel, { nonNullable: true });

  // Slider progress
  readonly sliderProgress = computed(() => {
    const val = this.sampleSize();
    return `${Math.round((val / 500) * 100)}%`;
  });

  // Generate button label
  readonly generateLabel = computed(() => `Generate ${this.sampleSize()} Data Points`);

  selectDistribution(shape: DistributionShape): void {
    this.distribution.set(shape);
  }

  onSampleSizeSlider(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.sampleSize.set(Number(target.value));
  }

  setSampleSize(n: number): void {
    this.sampleSize.set(n);
  }

  decrementOutliers(): void {
    const current = this.outliers();
    if (current > 0) {
      this.outliers.set(current - 1);
    }
  }

  incrementOutliers(): void {
    this.outliers.set(this.outliers() + 1);
  }

  setDecimalPlaces(dp: number): void {
    this.decimalPlaces.set(dp);
  }

  toggleAdvanced(): void {
    this.advancedOpen.set(!this.advancedOpen());
  }

  onLoadDataset(dataset: LibraryDataset): void {
    const colId = uuid();
    const config: DataInputConfig = {
      columns: [{ id: colId, header: dataset.columnLabel, colType: 'number' }],
      rows: dataset.values.map((v) => ({ [colId]: v })),
      rowCount: dataset.values.length,
      editableHeaders: true,
      allowAddColumns: false,
      allowAddRows: true,
    };
    this.generate.emit(config);
  }

  onGenerate(): void {
    const colId = uuid();
    const columnLabel = this.columnLabelCtrl.value || 'x';

    const values = generateData({
      distribution: this.distribution(),
      sampleSize: this.sampleSize(),
      minValue: this.minValueCtrl.value,
      maxValue: this.maxValueCtrl.value,
      mean: this.meanCtrl.value,
      stdDev: this.stdDevCtrl.value,
      center1: this.center1Ctrl.value,
      center2: this.center2Ctrl.value,
      spread: this.spreadCtrl.value,
      outliers: this.outliers(),
      decimalPlaces: this.decimalPlaces(),
      seed: this.seedCtrl.value,
      integerOnly: false,
      columnLabel,
    });

    const config: DataInputConfig = {
      columns: [{ id: colId, header: columnLabel, colType: 'number' }],
      rows: values.map((v) => ({ [colId]: v })),
      rowCount: values.length,
      editableHeaders: true,
      allowAddColumns: false,
      allowAddRows: true,
    };

    this.generate.emit(config);
  }
}
